import "pages/App.css";
import "react-toastify/dist/ReactToastify.min.css";
import "utils/i18n";

import * as queryString from "query-string";

import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import React, { Component, Suspense } from "react";
import {
  appStage,
  isImpersonatingCookieName,
  isLoggedInCookieName,
  nodeEnv,
} from "environment";
import { createPublicPerson, linkPublicPerson } from "slices/persons";
import {
  getAllAnonymousIds,
  getAnonymousId,
  replaceAnonymousId,
} from "utils/analytics";

import AnalyticsBlock from "pages/AnalyticsBlock";
import Cookies from "js-cookie";
import Error404 from "components/general/Error404";
import { ErrorTest } from "pages/ErrorTest";
import GlobalStyle from "styles/globalStyle";
import { PrivateRoute } from "components/router/PrivateRoute";
import { PuckToastContainer } from "components/general/Toast";
import { ShowEnv } from "pages/ShowEnv";
import { Unauthorized } from "pages/auth/Unauthorized";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import { cookieDomain } from "environment";
import importRetry from "utils/importRetry";
import { isLoggedIn } from "utils/api";
import { keepSessionAlive } from "slices/employers";

/* PUCK ADMIN PAGES */
const Impersonate = React.lazy(() =>
  importRetry(() =>
    import(
      /* webpackChunkName: "admin" */
      "pages/auth/Impersonate"
    )
  )
);

/* AUTH PAGES */
const ClaimAccount = React.lazy(() =>
  importRetry(() =>
    import(
      /* webpackChunkName: "auth" */
      "pages/auth/ClaimAccount"
    )
  )
);
const ClaimAccountSignup = React.lazy(() =>
  import(
    /* webpackChunkName: "auth" */
    "pages/auth/ClaimAccountSignup"
  )
);
const ClaimAccountProfile = React.lazy(() =>
  importRetry(() =>
    import(
      /* webpackChunkName: "auth" */
      "pages/auth/ClaimAccountProfile"
    )
  )
);
const ForgotPassword = React.lazy(() =>
  importRetry(() =>
    import(
      /* webpackChunkName: "auth" */
      "pages/auth/ForgotPassword"
    )
  )
);
const ResetPassword = React.lazy(() =>
  importRetry(() =>
    import(
      /* webpackChunkName: "auth" */
      "pages/auth/ResetPassword"
    )
  )
);
const Login = React.lazy(() =>
  importRetry(() =>
    import(
      /* webpackChunkName: "auth" */
      "pages/auth/Login"
    )
  )
);
// const Test = React.lazy(() =>
//   importRetry(() =>
//     import(
//       /* webpackChunkName: "auth" */
//       "pages/auth/Test"
//     )
//   )
// );
const Logout = React.lazy(() =>
  importRetry(() =>
    import(
      /* webpackChunkName: "auth" */
      "pages/auth/Logout"
    )
  )
);

/* DASHBOARD PAGES */
const Home = React.lazy(() =>
  importRetry(() =>
    import(
      /* webpackChunkName: "dashboard" */
      "pages/Home"
    )
  )
);
const ShareLinkDetail = React.lazy(() =>
  importRetry(() =>
    import(
      /* webpackChunkName: "dashboard" */
      "pages/dashboard/ShareLinkDetail"
    )
  )
);
const DashboardJobClipEdit = React.lazy(() =>
  importRetry(() =>
    import(
      /* webpackChunkName: "dashboard" */
      "pages/dashboard/JobClipEdit"
    )
  )
);
const DashboardJobClipDetail = React.lazy(() =>
  importRetry(() =>
    import(
      /* webpackChunkName: "dashboard" */
      "pages/dashboard/JobClipDetail"
    )
  )
);
const DashboardJobPageDetail = React.lazy(() =>
  importRetry(() =>
    import(
      /* webpackChunkName: "dashboard" */
      "pages/dashboard/JobPageDetail"
    )
  )
);
const DashboardJobPages = React.lazy(() =>
  importRetry(() =>
    import(
      /* webpackChunkName: "dashboard" */
      "pages/dashboard/JobPages"
    )
  )
);
const DashboardLeads = React.lazy(() =>
  importRetry(() =>
    import(
      /* webpackChunkName: "dashboard" */
      "pages/dashboard/Leads"
    )
  )
);
const DashboardScreeningQuestions = React.lazy(() =>
  importRetry(() =>
    import(
      /* webpackChunkName: "dashboard" */
      "pages/dashboard/ScreeningQuestions"
    )
  )
);
const DashboardScreeningQuestionsEdit = React.lazy(() =>
  importRetry(() =>
    import(
      /* webpackChunkName: "dashboard" */
      "pages/dashboard/ScreeningQuestionsEdit"
    )
  )
);
const DashboardTextToApply = React.lazy(() =>
  importRetry(() =>
    import(
      /* webpackChunkName: "dashboard" */
      "pages/dashboard/TextToApply"
    )
  )
);
// const DashboardPodcasts = React.lazy(() =>
//   importRetry(() =>
//     import(
//       /* webpackChunkName: "dashboard" */
//       "pages/dashboard/Podcasts"
//     )
//   )
// );
const DashboardContentLibrary = React.lazy(() =>
  importRetry(() =>
    import(
      /* webpackChunkName: "dashboard" */
      "pages/dashboard/ContentLibrary"
    )
  )
);
const DashboardSettingsProfile = React.lazy(() =>
  importRetry(() =>
    import(
      /* webpackChunkName: "dashboard" */
      "pages/dashboard/settings/Profile"
    )
  )
);
const DashboardSettingsAts = React.lazy(() =>
  importRetry(() =>
    import(
      /* webpackChunkName: "dashboard" */
      "pages/dashboard/settings/Ats"
    )
  )
);
const DashboardSettingsMembers = React.lazy(() =>
  importRetry(() =>
    import(
      /* webpackChunkName: "dashboard" */
      "pages/dashboard/settings/Members"
    )
  )
);
const DashboardSettingsSmartLinks = React.lazy(() =>
  importRetry(() =>
    import(
      /* webpackChunkName: "dashboard" */
      "pages/dashboard/settings/SmartLinks"
    )
  )
);
const DashboardSettingsApplicationReminders = React.lazy(() =>
  importRetry(() =>
    import(
      /* webpackChunkName: "dashboard" */
      "pages/dashboard/settings/ApplicationReminders"
    )
  )
);
const DashboardSettingsEmail = React.lazy(() =>
  importRetry(() =>
    import(
      /* webpackChunkName: "dashboard" */
      "pages/dashboard/settings/Email"
    )
  )
);
// const DashboardSmartLinks = React.lazy(() =>
//   importRetry(() =>
//     import(
//       /* webpackChunkName: "dashboard" */
//       "pages/dashboard/SmartLinks"
//     )
//   )
// );
const DashboardTeaserEdit = React.lazy(() =>
  importRetry(() =>
    import(
      /* webpackChunkName: "dashboard" */
      "pages/dashboard/TeaserEdit"
    )
  )
);
const DashboardTeasers = React.lazy(() =>
  importRetry(() =>
    import(
      /* webpackChunkName: "dashboard" */
      "pages/dashboard/Teasers"
    )
  )
);
const LeverCallback = React.lazy(() =>
  importRetry(() =>
    import(
      /* webpackChunkName: "dashboard" */
      "pages/ats/LeverCallback"
    )
  )
);
const OutlookCallback = React.lazy(() =>
  importRetry(() =>
    import(
      /* webpackChunkName: "dashboard" */
      "pages/email/OutlookCallback"
    )
  )
);
// const ContentPages = React.lazy(() =>
//   importRetry(() =>
//     import(
//       /* webpackChunkName: "dashboard" */
//       "pages/dashboard/ContentPages"
//     )
//   )
// );
const ContentPageEdit = React.lazy(() =>
  importRetry(() =>
    import(
      /* webpackChunkName: "dashboard" */
      "pages/dashboard/ContentPageEdit"
    )
  )
);
const SourcingPageDetail = React.lazy(() =>
  importRetry(() =>
    import(
      /* webpackChunkName: "dashboard" */
      "pages/dashboard/SourcingPageDetail"
    )
  )
);
const SourcingPages = React.lazy(() =>
  importRetry(() =>
    import(
      /* webpackChunkName: "dashboard" */
      "pages/dashboard/SourcingPages"
    )
  )
);
const SourcingPageWizard = React.lazy(() =>
  importRetry(() =>
    import(
      /* webpackChunkName: "dashboard" */
      "pages/dashboard/SourcingPageWizard"
    )
  )
);
const SourcingPagePreview = React.lazy(() =>
  importRetry(() =>
    import(
      /* webpackChunkName: "dashboard" */
      "pages/dashboard/SourcingPagePreview"
    )
  )
);
const InterviewGuideDetail = React.lazy(() =>
  importRetry(() =>
    import(
      /* webpackChunkName: "dashboard" */
      "pages/dashboard/InterviewGuideDetail"
    )
  )
);
const InterviewGuides = React.lazy(() =>
  importRetry(() =>
    import(
      /* webpackChunkName: "dashboard" */
      "pages/dashboard/InterviewGuides"
    )
  )
);
const OtherPageDetail = React.lazy(() =>
  importRetry(() =>
    import(
      /* webpackChunkName: "dashboard" */
      "pages/dashboard/OtherPageDetail"
    )
  )
);
const OtherPages = React.lazy(() =>
  importRetry(() =>
    import(
      /* webpackChunkName: "dashboard" */
      "pages/dashboard/OtherPages"
    )
  )
);
const ContentPageJobPageDetail = React.lazy(() =>
  importRetry(() =>
    import(
      /* webpackChunkName: "dashboard" */
      "pages/dashboard/ContentPageJobPageDetail"
    )
  )
);
const ContentPageJobPages = React.lazy(() =>
  importRetry(() =>
    import(
      /* webpackChunkName: "dashboard" */
      "pages/dashboard/ContentPageJobPages"
    )
  )
);
const DashboardPersonListDetail = React.lazy(() =>
  importRetry(() =>
    import(
      /* webpackChunkName: "dashboard" */
      "pages/dashboard/PersonListDetail"
    )
  )
);
const DashboardTalentPool = React.lazy(() =>
  importRetry(() =>
    import(
      /* webpackChunkName: "dashboard" */
      "pages/dashboard/TalentPool"
    )
  )
);
const DashboardTalentLists = React.lazy(() =>
  importRetry(() =>
    import(
      /* webpackChunkName: "dashboard" */
      "pages/dashboard/TalentLists"
    )
  )
);
const DashboardSequenceDetail = React.lazy(() =>
  importRetry(() =>
    import(
      /* webpackChunkName: "dashboard" */
      "pages/dashboard/SequenceDetail"
    )
  )
);
const DashboardSequences = React.lazy(() =>
  importRetry(() =>
    import(
      /* webpackChunkName: "dashboard" */
      "pages/dashboard/Sequences"
    )
  )
);
const DashboardSequenceEdit = React.lazy(() =>
  importRetry(() =>
    import(
      /* webpackChunkName: "dashboard" */
      "pages/dashboard/SequenceEdit"
    )
  )
);
const DashboardSequenceReview = React.lazy(() =>
  importRetry(() =>
    import(
      /* webpackChunkName: "dashboard" */
      "pages/dashboard/SequenceReview"
    )
  )
);

/* PUBLIC CANDIDATE PAGES */
const JobBoardPublic = React.lazy(() =>
  importRetry(() =>
    import(
      /* webpackChunkName: "job-board-public" */
      "pages/jobs/JobBoardPublic"
    )
  )
);
const JobPagePublic = React.lazy(() =>
  importRetry(() =>
    import(
      /* webpackChunkName: "job-page-public" */
      "pages/jobs/JobPagePublic"
    )
  )
);
const JobPageApplicationPublic = React.lazy(() =>
  importRetry(() =>
    import(
      /* webpackChunkName: "job-page-application-public" */
      "pages/jobs/JobPageApplicationPublic"
    )
  )
);
const JobClipPublic = React.lazy(() =>
  importRetry(() =>
    import(
      /* webpackChunkName: "job-clip-public" */
      "pages/jobs/JobClipPublic"
    )
  )
);
const JobClipTranscriptPublic = React.lazy(() =>
  importRetry(() =>
    import(
      /* webpackChunkName: "job-clip-transcript-public" */
      "pages/jobs/JobClipTranscriptPublic"
    )
  )
);
const AppReminderOptOut = React.lazy(() =>
  importRetry(() =>
    import(
      /* webpackChunkName: "app-reminder-opt-out" */
      "pages/jobs/AppReminderOptOut"
    )
  )
);
const SalaryCalculatorResults = React.lazy(() =>
  importRetry(() =>
    import(
      /* webpackChunkName: "salary-calculator" */
      "pages/salary_calculator/Results"
    )
  )
);
const SalaryCalculatorResultsLinked = React.lazy(() =>
  importRetry(() =>
    import(
      /* webpackChunkName: "salary-calculator" */
      "pages/salary_calculator/ResultsLinked"
    )
  )
);
const ContentPagePublic = React.lazy(() =>
  importRetry(() =>
    import(
      /* webpackChunkName: "content-page-public" */
      "pages/content_pages/ContentPagePublic"
    )
  )
);
const ContentPagePublicSales = React.lazy(() =>
  importRetry(() =>
    import(
      /* webpackChunkName: "content-page-public-sales" */
      "pages/content_pages/ContentPagePublicSales"
    )
  )
);
/* EMBED PAGE */
const Embed = React.lazy(() =>
  importRetry(() =>
    import(
      /* webpackChunkName: "embed" */
      "pages/embed/Embed"
    )
  )
);
/* PDF REPORT DOWNLOAD PAGE */
const PdfReportDownload = React.lazy(() =>
  importRetry(() =>
    import(
      /* webpackChunkName: "pdf-reports" */
      "pages/pdf_reports/Download"
    )
  )
);
/* PUBLIC DAILY SPOTLIGHT PAGES */
const DailySpotlightPublic = React.lazy(() =>
  importRetry(() =>
    import(
      /* webpackChunkName: "daily-spotlight-public" */
      "pages/marketing/DailySpotlightPublic"
    )
  )
);

class App extends Component {
  // Send a keep-alive ping to the server
  componentDidMount() {
    // 25m timer
    this.keepAliveInterval = setInterval(() => {
      this.keepAlive();
    }, 25 * 60 * 1000);

    // Make sure we have a Puck anonymousId set
    getAnonymousId();

    // Link anonymous
    this._linkPublicPerson();

    // Create short link person
    this._createShareLinkPerson();
  }

  componentWillUnmount() {
    // Probably not necessary, but can't hurt
    clearInterval(this.keepAliveInterval);
  }

  _linkPublicPerson = async () => {
    const { dispatch } = this.props;

    // See if have puckAnonymousId in query params
    const queryParams = queryString.parse(document.location.search);

    if (queryParams.puckAnonymousId) {
      const anonymousId = queryParams.puckAnonymousId;

      const payload = {
        anonymousIds: getAllAnonymousIds(anonymousId),
      };

      const resultAction = await dispatch(linkPublicPerson({ payload }));
      if (linkPublicPerson.fulfilled.match(resultAction)) {
        // Replace the Puck anonymousId cookie with this new ID
        replaceAnonymousId(anonymousId);
      }
    }
  };

  handleCandidateDataSave = (candidateData) => {
    localStorage.setItem("puck-candidate", JSON.stringify(candidateData));
    this.setState({ candidateDataUnlocked: true });

    const payload = {
      anonymousIds: getAllAnonymousIds(),
      employerPermalink: this.state.contentPage.employerProfile.permalink,
      person: {
        source: "content-page",
        email: candidateData.email,
        fullName: candidateData.fullName,
      },
    };
    if (payload.anonymousIds) {
      this.props.dispatch(createPublicPerson({ payload }));
    }

    this.setupPageWithCandidateData(candidateData);
  };

  _createShareLinkPerson = async () => {
    const { dispatch } = this.props;

    // See if have any share link params
    const queryParams = queryString.parse(document.location.search);

    if (
      (queryParams["p-fn"] ||
        queryParams["p-ln"] ||
        queryParams["p-un"] ||
        queryParams["p-e"]) &&
      queryParams["p-s"]
    ) {
      const payload = {
        anonymousIds: getAllAnonymousIds(),
        shareLinkPermalink: queryParams["p-s"],
        person: {
          source: "share-link",
        },
      };
      if (queryParams["p-fn"]) {
        payload.person.firstName = queryParams["p-fn"];
      }
      if (queryParams["p-ln"]) {
        payload.person.lastName = queryParams["p-ln"];
      }
      if (queryParams["p-un"]) {
        payload.person.fullName = queryParams["p-un"];
      }
      if (queryParams["p-e"]) {
        payload.person.email = queryParams["p-e"];
      }

      if (payload.anonymousIds) {
        dispatch(createPublicPerson({ payload }));
      }
    }
  };

  keepAlive() {
    const { user, keepSessionAlive } = this.props;

    if (isLoggedIn() && user?.employerId) {
      keepSessionAlive(user.employerId);

      // Extend the login-related cookies too
      Cookies.set(isLoggedInCookieName, "true", {
        domain: cookieDomain,
        expires: 365,
      });
      if (Cookies.get(isImpersonatingCookieName) === "true") {
        Cookies.set(isImpersonatingCookieName, "true", {
          domain: cookieDomain,
          expires: 365,
        });
      }
    }
  }

  render() {
    return (
      <>
        <GlobalStyle />
        <PuckToastContainer />
        <BrowserRouter>
          <Suspense fallback={null}>
            <Routes>
              <Route
                exact
                path="/auth/lever/callback"
                element={
                  <PrivateRoute>
                    <LeverCallback />
                  </PrivateRoute>
                }
              />
              <Route
                path="/auth/outlook/callback"
                element={
                  <PrivateRoute>
                    <OutlookCallback />
                  </PrivateRoute>
                }
              />
              <Route
                path="/login"
                element={<Navigate to="/employers/login" />}
              />
              <Route path="/employers/login" element={<Login />} />
              <Route
                path="/employers/claim-account"
                element={<ClaimAccount />}
              />
              <Route
                path="/employers/claim-account-signup"
                element={<ClaimAccountSignup />}
              />
              <Route
                path="/employers/claim-account-profile"
                element={<ClaimAccountProfile />}
              />
              <Route
                path="/employers/forgot-password"
                element={<ForgotPassword />}
              />
              <Route
                path="/employers/reset-password"
                element={<ResetPassword />}
              />
              <Route path="/logout" element={<Logout />} />
              <Route path="/analytics/block" element={<AnalyticsBlock />} />
              <Route
                path="/impersonate"
                element={
                  <PrivateRoute>
                    <Impersonate />
                  </PrivateRoute>
                }
              />
              <Route
                path="/"
                element={
                  <PrivateRoute>
                    <Home />
                  </PrivateRoute>
                }
              />
              <Route
                path="/employers"
                element={
                  <PrivateRoute>
                    <Home />
                  </PrivateRoute>
                }
              />

              {/* Embed */}
              <Route
                path="/job-clip/:jobClipPermalink/track/:jobClipTrackPermalink/embed"
                element={<Embed />}
              />
              <Route
                path="/job-clip/:jobClipPermalink/embed"
                element={<Embed />}
              />

              {/* Job Clips */}
              <Route
                path="/job-clip/:jobClipPermalink/transcript"
                element={<JobClipTranscriptPublic />}
              />
              <Route
                path="/job-clip/:jobClipPermalink"
                element={<JobClipPublic />}
              />

              {/* Job Boards */}
              <Route
                path="/job-board/:jobBoardPermalink/job/:jobPagePermalink/apply"
                element={<JobPageApplicationPublic />}
              />
              <Route
                path="/job-board/:jobBoardPermalink/job/:jobPagePermalink"
                element={<JobPagePublic />}
              />
              <Route
                path="/job-board/:jobBoardPermalink"
                element={<JobBoardPublic />}
              />

              {/* Content Pages */}
              <Route
                path="/page/:contentPagePermalink"
                element={<ContentPagePublic />}
              />

              {/* Sales */}
              <Route
                path="/sales/:employerPermalink/page/:contentPagePermalink"
                element={<ContentPagePublicSales />}
              />

              {/* Employer Sharing */}
              <Route
                path="/employers/sourcing-page/:sourcingPagePermalink/share-link/:shareLinkPermalink"
                element={
                  <PrivateRoute>
                    <ShareLinkDetail />
                  </PrivateRoute>
                }
              />
              <Route
                path="/employers/interview-guide/:interviewGuidePermalink/share-link/:shareLinkPermalink"
                element={
                  <PrivateRoute>
                    <ShareLinkDetail />
                  </PrivateRoute>
                }
              />
              <Route
                path="/employers/other-page/:sourcingPagePermalink/share-link/:shareLinkPermalink"
                element={
                  <PrivateRoute>
                    <ShareLinkDetail />
                  </PrivateRoute>
                }
              />
              <Route
                path="/employers/job-page/:jobPagePermalink/share-link/:shareLinkPermalink"
                element={
                  <PrivateRoute>
                    <ShareLinkDetail />
                  </PrivateRoute>
                }
              />
              <Route
                path="/employers/content-library/podcast/:jobClipPermalink/share-link/:shareLinkPermalink"
                element={
                  <PrivateRoute>
                    <ShareLinkDetail />
                  </PrivateRoute>
                }
              />

              {/* Employer Podcasts */}
              <Route
                path="/employers/content-library/podcast/:jobClipPermalink/edit"
                element={
                  <PrivateRoute>
                    <DashboardJobClipEdit />
                  </PrivateRoute>
                }
              />
              <Route
                path="/employers/content-library/podcast/:jobClipPermalink"
                element={
                  <PrivateRoute>
                    <DashboardJobClipDetail />
                  </PrivateRoute>
                }
              />
              <Route
                path="/employers/content-library"
                element={
                  <PrivateRoute>
                    <DashboardContentLibrary />
                  </PrivateRoute>
                }
              />

              {/* Employer Job Pages */}
              <Route
                path="/employers/job-page/:jobPagePermalink"
                element={
                  <PrivateRoute>
                    <DashboardJobPageDetail />
                  </PrivateRoute>
                }
              />
              <Route
                path="/employers/job-page"
                element={
                  <PrivateRoute>
                    <DashboardJobPages />
                  </PrivateRoute>
                }
              />

              {/* Employer Leads */}
              <Route
                path="/employers/lead/screening-question/:screeningSetPermalink/edit"
                element={
                  <PrivateRoute>
                    <DashboardScreeningQuestionsEdit />
                  </PrivateRoute>
                }
              />
              <Route
                path="/employers/lead/screening-question/new"
                element={
                  <PrivateRoute>
                    <DashboardScreeningQuestionsEdit />
                  </PrivateRoute>
                }
              />
              <Route
                path="/employers/lead/screening-question"
                element={
                  <PrivateRoute>
                    <DashboardScreeningQuestions />
                  </PrivateRoute>
                }
              />
              <Route
                path="/employers/lead"
                element={
                  <PrivateRoute>
                    <DashboardLeads />
                  </PrivateRoute>
                }
              />

              {/* Employer Text-to-Apply */}
              <Route
                path="/employers/text-to-apply"
                element={
                  <PrivateRoute>
                    <DashboardTextToApply />
                  </PrivateRoute>
                }
              />

              {/* Employer Smart Links */}
              {/* <PrivateRoute
                path={"/employers/smart-link"}
                component={DashboardSmartLinks}
              /> */}

              {/* Employer Video Clips */}
              <Route
                path="/employers/video-clip/edit"
                element={
                  <PrivateRoute>
                    <DashboardTeaserEdit />
                  </PrivateRoute>
                }
              />
              <Route
                path="/employers/video-clip"
                element={
                  <PrivateRoute>
                    <DashboardTeasers />
                  </PrivateRoute>
                }
              />

              {/* Employer Talent Pool */}
              <Route
                path="/employers/talent-pool/person/:personPermalink"
                element={
                  <PrivateRoute>
                    <DashboardTalentPool />
                  </PrivateRoute>
                }
              />
              <Route
                path="/employers/talent-pool"
                element={
                  <PrivateRoute>
                    <DashboardTalentPool />
                  </PrivateRoute>
                }
              />

              {/* Employer Talent Lists */}
              <Route
                path="/employers/talent-list/:personListPermalink"
                element={
                  <PrivateRoute>
                    <DashboardPersonListDetail />
                  </PrivateRoute>
                }
              />
              <Route
                path="/employers/talent-list"
                element={
                  <PrivateRoute>
                    <DashboardTalentLists />
                  </PrivateRoute>
                }
              />

              {/* Employer Sequences */}
              <Route
                path="/employers/sequence/:sequencePermalink/edit"
                element={
                  <PrivateRoute>
                    <DashboardSequenceEdit />
                  </PrivateRoute>
                }
              />
              <Route
                path="/employers/sequence/:sequencePermalink/review"
                element={
                  <PrivateRoute>
                    <DashboardSequenceReview />
                  </PrivateRoute>
                }
              />
              <Route
                path="/employers/sequence/:sequencePermalink"
                element={
                  <PrivateRoute>
                    <DashboardSequenceDetail />
                  </PrivateRoute>
                }
              />
              <Route
                path="/employers/sequence"
                element={
                  <PrivateRoute>
                    <DashboardSequences />
                  </PrivateRoute>
                }
              />

              {/* Employer Content Pages */}
              <Route
                path="/employers/page/:contentPagePermalink/preview"
                element={
                  <PrivateRoute>
                    <SourcingPagePreview />
                  </PrivateRoute>
                }
              />
              <Route
                path="/employers/page/:contentPagePermalink/edit"
                element={
                  <PrivateRoute>
                    <ContentPageEdit />
                  </PrivateRoute>
                }
              />
              <Route
                path="/employers/page/sourcing-page-wizard"
                element={
                  <PrivateRoute>
                    <SourcingPageWizard />
                  </PrivateRoute>
                }
              />
              <Route
                path="/employers/settings/email"
                element={
                  <PrivateRoute>
                    <DashboardSettingsEmail />
                  </PrivateRoute>
                }
              />
              <Route
                path="/employers/podcast/job-clip/:jobClipPermalink/edit"
                element={
                  <PrivateRoute>
                    <DashboardJobClipEdit />
                  </PrivateRoute>
                }
              />
              <Route
                path="/employers/sourcing-page/:sourcingPagePermalink"
                element={
                  <PrivateRoute>
                    <SourcingPageDetail />
                  </PrivateRoute>
                }
              />
              <Route
                path="/employers/sourcing-page"
                element={
                  <PrivateRoute>
                    <SourcingPages />
                  </PrivateRoute>
                }
              />
              <Route
                path="/employers/interview-guide/:interviewGuidePermalink"
                element={
                  <PrivateRoute>
                    <InterviewGuideDetail />
                  </PrivateRoute>
                }
              />
              <Route
                path="/employers/interview-guide"
                element={
                  <PrivateRoute>
                    <InterviewGuides />
                  </PrivateRoute>
                }
              />
              <Route
                path="/employers/content-page-job-page/:contentPageJobPagePermalink"
                element={
                  <PrivateRoute>
                    <ContentPageJobPageDetail />
                  </PrivateRoute>
                }
              />
              <Route
                path="/employers/content-page-job-page"
                element={
                  <PrivateRoute>
                    <ContentPageJobPages />
                  </PrivateRoute>
                }
              />
              <Route
                path="/employers/other-page/:otherPagePermalink"
                element={
                  <PrivateRoute>
                    <OtherPageDetail />
                  </PrivateRoute>
                }
              />
              <Route
                path="/employers/other-page"
                element={
                  <PrivateRoute>
                    <OtherPages />
                  </PrivateRoute>
                }
              />

              {/* Employer Settings */}
              <Route
                path="/employers/settings/profile"
                element={
                  <PrivateRoute>
                    <DashboardSettingsProfile />
                  </PrivateRoute>
                }
              />
              <Route
                path="/employers/settings/ats"
                element={
                  <PrivateRoute>
                    <DashboardSettingsAts />
                  </PrivateRoute>
                }
              />
              <Route
                path="/employers/settings/members"
                element={
                  <PrivateRoute>
                    <DashboardSettingsMembers />
                  </PrivateRoute>
                }
              />
              <Route
                path="/employers/settings/smart-links"
                element={
                  <PrivateRoute>
                    <DashboardSettingsSmartLinks />
                  </PrivateRoute>
                }
              />
              <Route
                path="/employers/settings/application-reminders"
                element={
                  <PrivateRoute>
                    <DashboardSettingsApplicationReminders />
                  </PrivateRoute>
                }
              />
              <Route
                path="/employers/settings"
                element={
                  <PrivateRoute>
                    <Navigate to="/employers/settings/ats" />
                  </PrivateRoute>
                }
              />

              {/* Employer PDF Reports */}
              <Route
                path="/employers/:employerPermalink/pdf-report/:pdfReportPermalink/download"
                element={
                  <PrivateRoute>
                    <PdfReportDownload />
                  </PrivateRoute>
                }
              />

              {/* App Reminders */}
              <Route
                path="/app-reminder/opt-out/:sequenceTargetPermalink"
                element={<AppReminderOptOut />}
              />

              {/* Salary Calculator */}
              <Route
                path="/salary-calculator/link/:permalink"
                element={<SalaryCalculatorResultsLinked />}
              />
              <Route
                path="/salary-calculator"
                element={<SalaryCalculatorResults />}
              />

              {/* Daily Spotlights */}
              <Route
                path="/daily-highlights/:date"
                element={<DailySpotlightPublic />}
              />
              <Route
                path="/daily-highlights"
                element={<DailySpotlightPublic />}
              />

              {(appStage === "uat" || nodeEnv === "development") && (
                <>
                  <Route path="/__env" element={<ShowEnv />} />
                  <Route path="/__error" element={<ErrorTest />} />
                  <Route path="/__unauthorized" element={<Unauthorized />} />
                </>
              )}
              <Route path="/404" element={<Error404 />} />
              <Route path="*" element={<Error404 />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </>
    );
  }
}

function mapStateToProps(state) {
  const { auth } = state;
  return {
    user: auth.user,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    dispatch,
    ...bindActionCreators(
      {
        keepSessionAlive,
        linkPublicPerson,
        createPublicPerson,
      },
      dispatch
    ),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(App);
